import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import { gridFilteredSortedRowIdsSelector, useGridApiContext } from '@mui/x-data-grid';
import { DownloadCsvAdditionalAction } from 'components/atoms/BaseDataGrid/DownloadCsvButton';
import { useSnackbar } from 'notistack';
import Papa from 'papaparse';
import React from 'react';
import { useCellTypeOptions } from 'utils/queryHooks/useCellTypeOptions';

type ExportCell = string | number;
type ExportPalette = 'light' | 'dark';

const escapeFormulaText = (value: string) => (/^[=+\-@\t\r]/.test(value) ? `'${value}` : value);

const getExportTable = (
  apiRef: ReturnType<typeof useGridApiContext>,
  currentCellTypeDisplayNames: Record<string, string>
) => {
  const columns = apiRef.current.getVisibleColumns().filter((column) => !column.disableExport);
  const rowIds = gridFilteredSortedRowIdsSelector(apiRef);
  const fields = columns.map((column) => column.field);
  const headers = columns.map((column) =>
    escapeFormulaText(column.field === 'id' ? 'Cell Type' : String(column.headerName ?? column.field))
  );
  const rows = rowIds.map((rowId) =>
    fields.map((field): ExportCell => {
      const value = apiRef.current.getCellValue(rowId, field);
      if (field === 'id') {
        const row = apiRef.current.getRow(rowId);
        const cellTypeId = String(value);
        return escapeFormulaText(String(currentCellTypeDisplayNames[cellTypeId] ?? row?.displayName ?? value ?? ''));
      }
      return typeof value === 'number' ? value : String(value ?? '');
    })
  );
  return { headers, rows };
};

const downloadBlob = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
};

const getRuleColors = (ruleValue: number, palette: ExportPalette) => {
  if (palette === 'dark') {
    if (ruleValue === -1) return { fill: '#333333', text: '#f5f5f5' };
    if (ruleValue === 0) return { fill: '#815474', text: '#ffffff' };
    if (ruleValue === 1) return { fill: '#25686a', text: '#ffffff' };
    return { fill: '#285e60', text: '#ffffff' };
  }
  if (ruleValue === -1) return { fill: '#f3f3f3', text: '#202124' };
  if (ruleValue === 0) return { fill: '#e5c0db', text: '#202124' };
  if (ruleValue === 1) return { fill: '#a6dfe0', text: '#202124' };
  return { fill: '#7dc8c9', text: '#202124' };
};

const createRulesPng = async (headers: string[], rows: ExportCell[][], palette: ExportPalette) => {
  const scale = 2;
  const firstColumnWidth = 220;
  const markerColumnWidth = 150;
  const titleHeight = 48;
  const headerHeight = 48;
  const rowHeight = 38;
  const legendHeight = 52;
  const width = firstColumnWidth + Math.max(0, headers.length - 1) * markerColumnWidth;
  const height = titleHeight + headerHeight + rows.length * rowHeight + legendHeight;
  const canvas = document.createElement('canvas');
  canvas.width = width * scale;
  canvas.height = height * scale;
  const context = canvas.getContext('2d');
  if (!context) throw new Error('PNG export is unavailable in this browser.');
  context.scale(scale, scale);

  const colors =
    palette === 'dark'
      ? { background: '#171717', header: '#2a2a2a', row: '#1e1e1e', border: '#555555', text: '#f5f5f5' }
      : { background: '#ffffff', header: '#e7e9eb', row: '#ffffff', border: '#c6c8ca', text: '#202124' };
  context.fillStyle = colors.background;
  context.fillRect(0, 0, width, height);
  context.font = '600 18px sans-serif';
  context.fillStyle = colors.text;
  context.textAlign = 'left';
  context.textBaseline = 'middle';
  context.fillText('Cell rules', 16, titleHeight / 2);

  const columnX = (columnIndex: number) =>
    columnIndex === 0 ? 0 : firstColumnWidth + (columnIndex - 1) * markerColumnWidth;
  const columnWidth = (columnIndex: number) => (columnIndex === 0 ? firstColumnWidth : markerColumnWidth);
  context.font = '600 14px sans-serif';
  headers.forEach((header, columnIndex) => {
    const x = columnX(columnIndex);
    const cellWidth = columnWidth(columnIndex);
    context.fillStyle = colors.header;
    context.fillRect(x, titleHeight, cellWidth, headerHeight);
    context.fillStyle = colors.border;
    context.strokeRect(x, titleHeight, cellWidth, headerHeight);
    context.fillStyle = colors.text;
    context.textAlign = columnIndex === 0 ? 'left' : 'center';
    context.fillText(
      header,
      columnIndex === 0 ? x + 14 : x + cellWidth / 2,
      titleHeight + headerHeight / 2,
      cellWidth - 20
    );
  });

  rows.forEach((row, rowIndex) => {
    const y = titleHeight + headerHeight + rowIndex * rowHeight;
    row.forEach((value, columnIndex) => {
      const x = columnX(columnIndex);
      const cellWidth = columnWidth(columnIndex);
      context.fillStyle = columnIndex === 0 ? colors.row : getRuleColors(Number(value), palette).fill;
      context.fillRect(x, y, cellWidth, rowHeight);
      context.strokeStyle = colors.border;
      context.strokeRect(x, y, cellWidth, rowHeight);
      context.fillStyle = columnIndex === 0 ? colors.text : getRuleColors(Number(value), palette).text;
      context.font = columnIndex === 0 ? '500 14px sans-serif' : '600 14px sans-serif';
      context.textAlign = columnIndex === 0 ? 'left' : 'center';
      context.fillText(
        String(value),
        columnIndex === 0 ? x + 14 : x + cellWidth / 2,
        y + rowHeight / 2,
        cellWidth - 20
      );
      if (columnIndex > 0 && Number(value) === 2) {
        context.strokeStyle = palette === 'dark' ? '#75bfc1' : '#397f80';
        context.beginPath();
        context.moveTo(x + 8, y + rowHeight - 4);
        context.lineTo(x + cellWidth - 8, y + 4);
        context.moveTo(x + 8, y + 4);
        context.lineTo(x + cellWidth - 8, y + rowHeight - 4);
        context.stroke();
      }
    });
  });

  const legendTop = titleHeight + headerHeight + rows.length * rowHeight + 8;
  const legend = [
    { value: -1, label: 'Indifferent' },
    { value: 0, label: 'Must be negative' },
    { value: 1, label: 'Must be positive' },
    { value: 2, label: 'At least one positive' },
  ];
  let legendX = 14;
  context.font = '12px sans-serif';
  context.textAlign = 'left';
  legend.forEach(({ value, label }) => {
    const swatch = getRuleColors(value, palette);
    context.fillStyle = swatch.fill;
    context.fillRect(legendX, legendTop + 7, 22, 22);
    context.strokeStyle = colors.border;
    context.strokeRect(legendX, legendTop + 7, 22, 22);
    context.fillStyle = swatch.text;
    context.textAlign = 'center';
    context.fillText(String(value), legendX + 11, legendTop + 18);
    context.fillStyle = colors.text;
    context.textAlign = 'left';
    context.fillText(label, legendX + 28, legendTop + 18);
    legendX += 42 + context.measureText(label).width;
  });

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('Could not create PNG export.'))), 'image/png');
  });
};

export const useCellRulesExportActions = () => {
  const apiRef = useGridApiContext();
  const { idToDisplayNameMap } = useCellTypeOptions();
  const { enqueueSnackbar } = useSnackbar();
  const table = () => getExportTable(apiRef, idToDisplayNameMap);

  const onDownloadCsv = () => {
    const { headers, rows } = table();
    const csv = Papa.unparse({ fields: headers, data: rows }, { escapeFormulae: false });
    downloadBlob(new Blob(['\uFEFF', csv], { type: 'text/csv;charset=utf-8' }), 'cell-rules.csv');
  };

  const onCopy = async () => {
    try {
      const { headers, rows } = table();
      await navigator.clipboard.writeText(
        Papa.unparse({ fields: headers, data: rows }, { delimiter: '\t', escapeFormulae: false })
      );
      enqueueSnackbar('Cell rules copied as a tab-separated table.', { variant: 'success' });
    } catch {
      enqueueSnackbar('Could not copy cell rules to the clipboard.', { variant: 'error' });
    }
  };

  const onDownloadPng = async (palette: ExportPalette) => {
    try {
      const { headers, rows } = table();
      const blob = await createRulesPng(headers, rows, palette);
      downloadBlob(blob, `cell-rules-${palette}.png`);
    } catch {
      enqueueSnackbar('Could not create the cell-rules image.', { variant: 'error' });
    }
  };

  const additionalActions: DownloadCsvAdditionalAction[] = [
    {
      label: 'Copy table',
      icon: <ContentCopyOutlinedIcon fontSize="small" sx={{ mr: 1 }} />,
      onClick: () => void onCopy(),
    },
    {
      label: 'Download PNG (light)',
      icon: <ImageOutlinedIcon fontSize="small" sx={{ mr: 1 }} />,
      onClick: () => void onDownloadPng('light'),
    },
    {
      label: 'Download PNG (dark)',
      icon: <ImageOutlinedIcon fontSize="small" sx={{ mr: 1 }} />,
      onClick: () => void onDownloadPng('dark'),
    },
  ];

  return { onDownloadCsv, additionalActions };
};
